import { Controller } from '@hotwired/stimulus';
import { dropdownMaxHeightPx } from '../../utils/dropdown_max_height';

export default class extends Controller {
  static targets = ['button', 'dropdown', 'wrapper', 'helper', 'placeholder', 'template', 'options', 'optionTemplate', 'limitError'];
  static values = {
    showAvatars: { type: Boolean, default: false },
    maxVisibleChips: Number, // maximum number of chips to show before showing "+X" chip
    direction: { type: String, default: 'auto' }, // 'auto' | 'bottom' | 'top'
    minWidth: Number,
    maxWidth: Number,
  };

  connect() {
    this.selectedValues = new Map();
    this.dropdownEl = this.dropdownTarget;
    this.dropdownOriginParent = this.dropdownEl.parentElement;
    this.portalRoot = this._ensurePortalRoot();

    this.syncInitialValues();

    const hasError = this.buttonTarget.dataset.hasError === 'true';
    if (hasError) this.showError();

    this._bindFormSubmitGuard();
  }

  disconnect() {
    this._unbindDropdownHandlers();
    this._teardownOutside();
    this._detachObservers();
    this._unbindFormSubmitGuard();
    if (this.dropdownEl && this.dropdownEl.parentElement === this.portalRoot) {
      this.dropdownOriginParent.appendChild(this.dropdownEl);
    }
  }

  _bindFormSubmitGuard() {
    this.parentForm = this.element.closest('form');
    if (!this.parentForm) return;

    this._onFormSubmit = () => {
      if (this.dropdownEl && this.dropdownEl.parentElement === this.portalRoot) {
        this.dropdownOriginParent.appendChild(this.dropdownEl);
      }
    };
    this.parentForm.addEventListener('submit', this._onFormSubmit, true);
  }

  _unbindFormSubmitGuard() {
    if (this.parentForm && this._onFormSubmit) {
      this.parentForm.removeEventListener('submit', this._onFormSubmit, true);
    }
    this.parentForm = null;
    this._onFormSubmit = null;
  }

  toggle(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.isDropdownOpen ? this.closeDropdown() : this.openDropdown();
  }

  toggleOption(event) {
    const checkbox = event.target;
    const value = checkbox.dataset.value;
    const name = checkbox.dataset.label || checkbox.dataset.name;
    const color = checkbox.dataset.color;
    const textColor = checkbox.dataset.textColor;
    const maxSelections = this.element.dataset.maxSelections;

    if (checkbox.checked) {
      if (maxSelections && this.selectedValues.size >= parseInt(maxSelections, 10)) {
        checkbox.checked = false;
        return;
      }
      this.selectedValues.set(value, { name, color, textColor });
    } else {
      this.selectedValues.delete(value);
    }

    this.refreshChips();
    this.updateDisabledState();
  }

  removeOption(event) {
    event.preventDefault();
    event.stopPropagation();

    const value = event.currentTarget.dataset.value;
    this.selectedValues.delete(value);

    const checkbox = this.dropdownEl.querySelector(
      `input[type="checkbox"][data-value="${CSS.escape(value)}"]`,
    );
    if (checkbox) {
      checkbox.checked = false;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.refreshChips();
    this.updateDisabledState();
  }

  clearSelection() {
    this.selectedValues = new Map();
    this.dropdownEl.querySelectorAll('input[type="checkbox"][data-value]').forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.refreshChips();
    this.updateDisabledState();
  }

  syncInitialValues() {
    const checkboxes = this.dropdownEl.querySelectorAll('input[type="checkbox"][data-value]');
    checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        const displayName = checkbox.dataset.label || checkbox.dataset.name;
        const color = checkbox.dataset.color;
        const textColor = checkbox.dataset.textColor;
        this.selectedValues.set(checkbox.dataset.value, { name: displayName, color, textColor });
      }
    });
    this.refreshChips();
    this.updateDisabledState();
  }

  refreshOptions(data) {
    this.selectedValues = new Map();
    this.buttonTarget.innerHTML = ''
    this.optionsTarget.innerHTML = ''

    data.forEach((option) => {
      const clone = this.optionTemplateTarget.content.cloneNode(true)
      const label = clone.querySelector("span")
      const input = clone.querySelector("input")

      label.textContent = option.name
      input.value = option.id
      input.dataset.value = option.id
      input.dataset.name = option.name

      this.optionsTarget.appendChild(clone)
    })

    this.syncInitialValues()
  }

  refreshChips() {
    const chipContainer = this.buttonTarget;
    chipContainer.innerHTML = '';

    if (this.selectedValues.size === 0) {
      chipContainer.innerHTML = `<span data-multi-select-chip-target="placeholder" class="text-black-400">${this.buttonTarget.dataset.placeholder}</span>`;
      return;
    }

    const maxVisible = this.hasMaxVisibleChipsValue ? this.maxVisibleChipsValue : null;
    const entries = Array.from(this.selectedValues.entries());
    const hasOverflow = maxVisible && entries.length > maxVisible;
    const visibleEntries = hasOverflow ? entries.slice(0, maxVisible) : entries;

    visibleEntries.forEach(([value, data]) => {
      const safeValue = value.trim();
      const name = typeof data === 'string' ? data : data.name;
      const color = typeof data === 'object' ? data.color : null;
      const textColor = typeof data === 'object' ? data.textColor : null;

      const clone = this.templateTarget.content.cloneNode(true);
      const chip = clone.querySelector("span");
      const button = clone.querySelector("button");
      if (button) { button.dataset.value = safeValue }

      const checkbox = this.dropdownEl.querySelector(
        `input[type="checkbox"][data-value="${CSS.escape(safeValue)}"]`
      );

      const avatarUrl = checkbox?.dataset.avatarUrl;
      const hasAvatar = avatarUrl && avatarUrl !== '' && avatarUrl !== 'null' && avatarUrl !== 'undefined';

      if (this.showAvatarsValue) {
        const avatarDiv = document.createElement('div');
        avatarDiv.className = 'w-20 h-20 rounded-full border border-black-400 flex items-center justify-center shrink-0';

        if (hasAvatar) {
          avatarDiv.className += ' overflow-hidden';
          const img = document.createElement('img');
          img.src = avatarUrl;
          img.alt = name || '';
          img.className = 'w-full h-full object-cover';
          avatarDiv.appendChild(img);
        } else {
          avatarDiv.className += ' bg-black-50 text-black-400 font-medium';
          const initials = (name || '').split(' ').map(w => w[0]?.toUpperCase()).join('').slice(0, 2) || '?';
          const initialsSpan = document.createElement('span');
          initialsSpan.className = 'text-[11px]';
          initialsSpan.textContent = initials;
          avatarDiv.appendChild(initialsSpan);
        }

        chip.insertBefore(avatarDiv, chip.firstChild);
      }

      chip.dataset.color = color;
      chip.dataset.textColor = textColor;
      chip.style.backgroundColor = color || "#f3f4f6";
      chip.style.color = textColor || "#111827";
      chip.style.borderColor = color || "#e5e7eb";
      if (button) button.style.color = chip.style.color;

      chip.dataset.value = safeValue;

      const textNode = document.createTextNode(name);
      chip.insertBefore(textNode, button);

      chipContainer.appendChild(chip);
    });

    // Add "+X" chip if there are more items
    if (hasOverflow) {
      const remainingCount = entries.length - maxVisible;
      const overflowChip = document.createElement('span');
      overflowChip.className = 'border border-brand-100 pl-4 py-4 pr-6 bg-transparent rounded-[20px] flex items-center gap-6 text-brand-1000 text-paragraph-s';
      overflowChip.dataset.overflow = 'true';

      const labelSpan = document.createElement('span');
      labelSpan.textContent = `+${remainingCount}`;
      overflowChip.appendChild(labelSpan);

      chipContainer.appendChild(overflowChip);
    }
  }

  updateDisabledState() {
    const maxSelections = this.element.dataset.maxSelections;
    if (!maxSelections) return;

    const limit = parseInt(maxSelections);
    const checkboxes = this.element.querySelectorAll('input[type="checkbox"][data-value]');

    checkboxes.forEach((checkbox) => {
      const label = checkbox.closest('label');
      if (this.selectedValues.size >= limit && !checkbox.checked) {
        checkbox.disabled = true;
        label.classList.add('opacity-50', 'cursor-not-allowed');

        label.addEventListener('click', (event) => {
          if (checkbox.disabled) {
            event.preventDefault();
            if (this.hasLimitErrorTarget) {
              this.limitErrorTarget.classList.remove('hidden');
            }
          }
        });
      } else {
        checkbox.disabled = false;
        label.classList.remove('opacity-50', 'cursor-not-allowed');
      }
    });
  }

  openDropdown() {
    if (this.isDropdownOpen) return;

    // teleport to portal
    this.portalRoot.appendChild(this.dropdownEl);

    this.dropdownEl.classList.remove('hidden');
    this.dropdownEl.classList.add('invisible', 'ss-transition-none', 'opacity-0', 'scale-95');

    this.buttonTarget.setAttribute('aria-expanded', 'true');

    this._positionDropdown();
    this._bindDropdownHandlers();

    void this.dropdownEl.offsetHeight;

    this.dropdownEl.classList.remove('ss-transition-none', 'invisible');

    requestAnimationFrame(() => {
      this.dropdownEl.classList.remove('opacity-0', 'scale-95');
      this.dropdownEl.classList.add('opacity-100', 'scale-100');
    });

    this._setupOutside();
    this._attachObservers();

    this.isDropdownOpen = true;
  }

  closeDropdown() {
    if (!this.isDropdownOpen) return;

    this.dropdownEl.classList.add('opacity-0', 'scale-95');
    this.dropdownEl.classList.remove('opacity-100', 'scale-100');

    const onEnd = () => {
      this.dropdownEl.classList.add('hidden');
      this.dropdownEl.removeEventListener('transitionend', onEnd);
      // Move dropdown back to original parent so Stimulus targets work
      this.dropdownOriginParent.appendChild(this.dropdownEl);
    };
    this.dropdownEl.addEventListener('transitionend', onEnd);

    this.buttonTarget.setAttribute('aria-expanded', 'false');

    this._unbindDropdownHandlers();
    this._teardownOutside();
    this._detachObservers();

    this.isDropdownOpen = false;
  }

  showError() {
    if (this.hasWrapperTarget) {
      this.wrapperTarget.classList.add('border-red-300');
      this.wrapperTarget.classList.add('text-red-1000');
      this.wrapperTarget.classList.add('placeholder-red-1000');
      this.wrapperTarget.classList.add('has-error');
    }

    if (this.hasButtonTarget) {
      this.buttonTarget.classList.add('border-red-300');
      this.buttonTarget.classList.add('text-red-1000');
      this.buttonTarget.classList.add('placeholder-red-1000');
      this.buttonTarget.classList.add('has-error');
    }

    if (this.hasHelperTarget) {
      this.helperTarget.classList.add('text-red-1000');
    }

    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add('text-red-1000');
    }
  }

  // === portal / positioning ===
  _ensurePortalRoot() {
    let root = document.getElementById('ui-layer-root');
    if (!root) {
      root = document.createElement('div');
      root.id = 'ui-layer-root';
      root.classList.add('ui-layer-root');
      document.body.appendChild(root);
    }
    return root;
  }

  _positionDropdown() {
    const btnRect = this.buttonTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const M = 8;

    const minW = this.hasMinWidthValue ? this.minWidthValue : 200;
    const maxW = this.hasMaxWidthValue ? this.maxWidthValue : Infinity;
    const width = Math.max(minW, Math.min(btnRect.width, maxW));

    this.dropdownEl.classList.add('ss-no-max-h');
    const naturalH = this.dropdownEl.offsetHeight || 300;
    this.dropdownEl.classList.remove('ss-no-max-h');

    const spaceBelow = vh - btnRect.bottom - M;
    const spaceAbove = btnRect.top - M;
    const wantsTop = this.directionValue === 'top';
    const wantsBottom = this.directionValue === 'bottom';
    const openUp = wantsTop ? true : wantsBottom ? false
      : spaceBelow < Math.min(naturalH, 320) && spaceAbove > spaceBelow;

    let top = openUp ? Math.max(M, btnRect.top - Math.min(naturalH, spaceAbove)) : btnRect.bottom;
    const maxH = openUp ? spaceAbove : spaceBelow;

    let left = btnRect.left;
    if (left + width + M > vw) left = Math.max(M, vw - width - M);
    if (left < M) left = M;

    this._setVar('--ss-left', `${Math.round(left)}px`);
    this._setVar('--ss-top', `${Math.round(top)}px`);
    this._setVar('--ss-width', `${Math.round(width)}px`);
    this._setVar('--ss-max-h', `${dropdownMaxHeightPx(naturalH, maxH)}px`);
  }

  _setVar(name, value) {
    this.dropdownEl.style.setProperty(name, value);
  }

  // === outside / observers ===
  _setupOutside() {
    if (this.outsideBound) return;
    this._onDocPointerDown = (e) => {
      const t = e.target;
      const insideDropdown = this.dropdownEl.contains(t);
      const insideComponent = this.element.contains(t);
      if (!insideDropdown && !insideComponent) this.closeDropdown();
    };
    this._onDocKeydown = (e) => {
      if (e.key === 'Escape') this.closeDropdown();
    };

    document.addEventListener('pointerdown', this._onDocPointerDown, true);
    document.addEventListener('keydown', this._onDocKeydown);
    this.outsideBound = true;
  }

  _teardownOutside() {
    if (!this.outsideBound) return;
    document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    document.removeEventListener('keydown', this._onDocKeydown);
    this.outsideBound = false;
  }

  _attachObservers() {
    this._reposition = () => {
      if (this.isDropdownOpen) this._positionDropdown();
    };
    this._onScroll = (e) => {
      if (this.dropdownEl && this.dropdownEl.contains(e.target)) return;
      this._reposition();
    };
    window.addEventListener('scroll', this._onScroll, true);
    window.addEventListener('resize', this._reposition);
    this.ro = new ResizeObserver(this._reposition);
    this.ro.observe(this.buttonTarget);
    if (window.visualViewport) {
      this.vvo = { onResize: this._reposition, onScroll: this._reposition };
      window.visualViewport.addEventListener('resize', this.vvo.onResize);
      window.visualViewport.addEventListener('scroll', this.vvo.onScroll);
    }
  }

  _detachObservers() {
    window.removeEventListener('scroll', this._onScroll, true);
    window.removeEventListener('resize', this._reposition);
    this.ro?.disconnect();
    this.ro = undefined;
    if (this.vvo) {
      window.visualViewport.removeEventListener('resize', this.vvo.onResize);
      window.visualViewport.removeEventListener('scroll', this.vvo.onScroll);
      this.vvo = undefined;
    }
  }

  // === dropdown bindings (after teleport) ===
  _bindDropdownHandlers() {
    if (this._dropdownHandlersBound || !this.dropdownEl) return;

    this._onDropdownChange = (e) => {
      const input = e.target?.closest?.('input[type="checkbox"]');
      if (input && this.dropdownEl.contains(input)) {
        const value = input.dataset.value;
        const name = input.dataset.label || input.dataset.name;
        const color = input.dataset.color;
        const textColor = input.dataset.textColor;

        if (input.checked) {
          const maxSelections = this.element.dataset.maxSelections;
          if (maxSelections && this.selectedValues.size >= parseInt(maxSelections, 10)) {
            input.checked = false;
            return;
          }
          this.selectedValues.set(value, { name, color, textColor });
        } else {
          this.selectedValues.delete(value);
        }

        this.refreshChips();
        this.updateDisabledState();
        // Dispatch change event to notify parent controllers (e.g., completion-quote-form)
        this.element.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    this._onDropdownClick = (e) => {
      // Don't intercept clicks directly on checkbox or its wrapper
      if (e.target.matches('input[type="checkbox"]') ||
          e.target.closest('.relative.box-border')) {
        return;
      }

      const li = e.target?.closest?.('li');
      if (li && this.dropdownEl.contains(li)) {
        const input = li.querySelector('input[type="checkbox"]');
        if (input) {
          input.checked = !input.checked;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    this.dropdownEl.addEventListener('change', this._onDropdownChange);
    this.dropdownEl.addEventListener('click', this._onDropdownClick);
    this._dropdownHandlersBound = true;
  }

  _unbindDropdownHandlers() {
    if (!this._dropdownHandlersBound || !this.dropdownEl) return;
    this.dropdownEl.removeEventListener('change', this._onDropdownChange);
    this.dropdownEl.removeEventListener('click', this._onDropdownClick);
    this._dropdownHandlersBound = false;
    this._onDropdownChange = null;
    this._onDropdownClick = null;
  }
}
